import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { CodeSnippet } from '@backstage/core-components';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Button,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItem,
  makeStyles,
  Popover,
  Tooltip,
  Typography,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import { Alert } from '@material-ui/lab';
import { restClientRef } from '@runway/devkit';
import { RestClientRequestConfig } from '@runway/devkit-common';

import { useAsync } from 'react-use';
import { AaTemplate, catalogProjectsServiceApiRef } from '../../services/catalogProjects.service';
import { AboutField } from './AboutField';
import { useState, MouseEvent } from 'react';

const RUNWAY_APP_SHORTNAME_ANNOTATION = 'runway.aa.com/app-shortname';
const RUNWAY_INTENT_ANNOTATION = 'runway.aa.com/intent';
const RUNWAY_SQUAD_IDS_ANNOTATION = 'runway.aa.com/squad-ids';

const useStyles = makeStyles(theme => ({
  code: {
    borderRadius: 6,
    margin: `${theme.spacing(2)}px 0px`,
    background: theme.palette.type === 'dark' ? '#444' : '#fff',
  },
  description: {
    wordBreak: 'break-word',
  },
}));

type Props = {
  entity: Entity;
};

type ApplicationUrlResource =
  | { kind: 'Ingress'; spec?: { rules?: { host?: string }[] } }
  | { kind: 'Service'; spec?: { externalName?: string } }
  | { kind: 'VirtualService'; spec?: { hosts?: string[]; gateways?: string[] } };

type ApplicationUrlResponse = {
  items: {
    resources: { type: string; resources: ApplicationUrlResource[] }[];
    errors?: { errorType: string; message?: string; statusCode?: number; resourcePath?: string }[];
  }[];
};

const NoURLPopover = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handlePopoverClick = (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const renderTroubleshootingMessages = () => (
    <List dense>
      <ListItem dense disableGutters>
        <Typography variant="body2" paragraph>
          Make sure that the{' '}
          <b>
            <a
              href="https://developer.aa.com/docs/default/component/runway/getting-started/userguides/catalog/#backstage-kubernetes-label-selector"
              target="_blank"
            >
              <code>argocd/app-selector</code> in your <code>catalog-info.yaml</code> file and the backstageName
              annotation in your <code>webapp.yaml</code>
            </a>
          </b>{' '}
          match
        </Typography>
      </ListItem>
      <ListItem dense disableGutters>
        <Typography variant="body2" paragraph>
          Make sure your application has an Ingress or VirtualService with a concrete hostname and labels matching the
          catalog entity's Kubernetes selector. VirtualServices must reference an ingress gateway. Check the{' '}
          <b>
            <a
              href="https://developer.aa.com/docs/default/component/runway/getting-started/userguides/webapp/#annotations"
              target="_blank"
            >
              traffic configuration in your <code>webapp.yaml</code>
            </a>
          </b>
          . Read more about Application URLs{' '}
          <b>
            <a
              href="https://developer.aa.com/docs/default/component/runway/getting-started/userguides/created-apps/#application-urls"
              target="_blank"
            >
              here
            </a>
          </b>
        </Typography>
      </ListItem>
    </List>
  );
  return (
    <>
      <IconButton onClick={handlePopoverClick} aria-label="no-url-helper">
        <InfoIcon />
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        onClose={handleClose}
        open={!!anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{ style: { width: '100%' } }}
        style={{ width: '25%' }}
      >
        <Alert severity="info" aria-label="no-url-troubleshoot">
          <Typography variant="body2">
            Are you expecting a URL here? Use the suggestions below to troubleshoot:
          </Typography>
          {renderTroubleshootingMessages()}
        </Alert>
      </Popover>
    </>
  );
};

export const AaAboutContent = ({ entity }: Props) => {
  const configApi = useApi(configApiRef);
  const classes = useStyles();

  const catalogProjectsApi = useApi(catalogProjectsServiceApiRef);
  const identityApi = useApi(identityApiRef);
  const restClient = useApi(restClientRef);

  const [aaInfo, setAaInfo] = useState<AaTemplate>();

  const squadURLs = useAsync(async () => {
    const squadLinks = [];
    if (aaInfo?.squads) {
      for (const squadID of aaInfo.squads) {
        const URL = squadID ? `https://squad360.aa.com/squad/view/${squadID}` : '';
        squadLinks.push({ label: squadID, url: URL });
      }
    }
    return squadLinks;
  }, [entity, aaInfo]);

  const getSquadLabel = () => {
    if (squadURLs.value) {
      if (squadURLs.value?.length > 1) {
        return 'Squads';
      }
    }
    return 'Squad';
  };

  const appUrlsResp = useAsync(async () => {
    const { token } = await identityApi.getCredentials();
    const options: RestClientRequestConfig = {
      method: 'POST',
      url: `${configApi.getString('backend.baseUrl')}/api/kubernetes/resources/custom/query`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token ?? ''}`,
      },
      data: {
        entityRef: stringifyEntityRef(entity),
        auth: {},
        customResources: [
          { group: '', apiVersion: 'v1', plural: 'services' },
          { group: 'networking.k8s.io', apiVersion: 'v1', plural: 'ingresses' },
          { group: 'networking.istio.io', apiVersion: 'v1', plural: 'virtualservices' },
        ],
      },
    };
    const kubernetesResp = await restClient.request<ApplicationUrlResponse>(options);
    const appUrls: string[] = [];
    for (const cluster of kubernetesResp.data.items) {
      for (const response of cluster.resources) {
        if (response.type !== 'customresources') {
          continue;
        }
        for (const resource of response.resources) {
          if (resource.kind === 'Ingress') {
            const host = resource.spec?.rules?.[0]?.host;
            if (host) {
              appUrls.push(`https://${host}`);
            }
          } else if (resource.kind === 'Service' && resource.spec?.externalName) {
            appUrls.push(`http://${resource.spec.externalName}`);
          } else if (
            resource.kind === 'VirtualService' &&
            resource.spec?.gateways?.some(gateway => gateway !== 'mesh')
          ) {
            for (const host of resource.spec?.hosts ?? []) {
              if (host.includes('.') && !host.includes('*')) {
                appUrls.push(`https://${host}`);
              }
            }
          }
        }
      }
    }
    return {
      urls: [...new Set(appUrls)],
      hasErrors: kubernetesResp.data.items.some(cluster =>
        cluster.errors?.some(
          error =>
            !(
              error.errorType === 'NOT_FOUND' &&
              error.statusCode === 404 &&
              /\/apis\/networking\.istio\.io\/v1\/(?:namespaces\/[^/]+\/)?virtualservices$/.test(
                error.resourcePath ?? '',
              )
            ),
        ),
      ),
    };
  }, [entity]);
  const appUrl = appUrlsResp.value?.urls ?? [];

  const aaInfoAsyncState = useAsync(async () => {
    const annotations = entity.metadata.annotations ?? {};

    const yaml = await catalogProjectsApi.getAaYaml(entity);

    const data = {
      appShortName: annotations[RUNWAY_APP_SHORTNAME_ANNOTATION] || '',
      squads: annotations[RUNWAY_SQUAD_IDS_ANNOTATION]?.split(',').map(s => s.trim()) || [],
      intent: annotations[RUNWAY_INTENT_ANNOTATION] || '',
    };

    // fetch from GitHub custom properties if any of the values are missing
    if (!data.appShortName || data.squads.length === 0 || !data.intent) {
      try {
        const github = await catalogProjectsApi.getRepoCustomProperties(entity);
        data.appShortName ||= github.appShortName;
        if (data.squads.length === 0) data.squads = github.squadID;
        data.intent ||= github.intent;
      } catch {
        // use existing values or aa yaml
      }
    }

    data.appShortName ||= yaml.cmdbAppShortName;
    if (data.squads.length === 0) data.squads = yaml.squads;

    setAaInfo({
      cmdbAppShortName: data.appShortName,
      squads: data.squads,
      apiVersion: yaml.apiVersion,
      intent: data.intent,
    });
  }, [entity]);

  const ENTITY_YAML = `apiVersion: "0.2.3"
cmdbAppShortName:
  - DevRunway
squads:
  - 12345
  - 23456`;

  const getCmdbAppShortName = () => aaInfo?.cmdbAppShortName || (entity?.metadata?.archerID as string);

  const getApiVersion = () => aaInfo?.apiVersion;

  return (
    <Grid container>
      <Grid container item xs={12} spacing={3}>
        <AboutField label="URL" gridSizes={{ xs: 12 }}>
          {appUrlsResp.loading && <LinearProgress />}
          {appUrlsResp.error && (
            <Alert severity="error">Unable to retrieve application URLs. Please try again later.</Alert>
          )}
          {appUrlsResp.value?.hasErrors && (
            <Alert severity="warning">Some clusters could not be queried. Application URLs may be incomplete.</Alert>
          )}
          {!appUrlsResp.loading && !appUrlsResp.error && !appUrlsResp.value?.hasErrors && appUrl.length === 0 && (
            <Typography variant="body2" paragraph className={classes.description}>
              No URL <NoURLPopover />
            </Typography>
          )}
          {!appUrlsResp.loading &&
            appUrl.length > 0 &&
            appUrl.map((url: string) => {
              if (!url.includes('svc.cluster.local')) {
                return (
                  <Grid container key={url}>
                    <Grid item>
                      <Link target="_blank" href={url} key={url}>
                        {url}
                      </Link>
                    </Grid>
                  </Grid>
                );
              }
              return (
                <Grid container key={url}>
                  <Grid item>{url}</Grid>
                  <Grid item style={{ marginLeft: 2 }}>
                    <Tooltip
                      title="Use this for connectivity within the mesh. The service mesh wraps this connectivity with SSL"
                      aria-label="Use this for connectivity within the mesh. The service mesh wraps this connectivity with SSL"
                    >
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Grid>
                </Grid>
              );
            })}
        </AboutField>
        <AboutField label={getSquadLabel()} urls={squadURLs.value} gridSizes={{ xs: 12, sm: 6, lg: 4 }} />
        <AboutField
          label="CMDB Application Shortname"
          value={getCmdbAppShortName()}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        />
        {aaInfo?.intent && (
          <AboutField label="Repository Intent" value={aaInfo.intent} gridSizes={{ xs: 12, sm: 6, lg: 4 }} />
        )}
      </Grid>
      {!squadURLs.loading &&
      !aaInfoAsyncState.loading &&
      ([getCmdbAppShortName(), getApiVersion()].includes(undefined) || squadURLs.value?.length === 0) ? (
        <Grid container alignItems="flex-start" item xs={12} spacing={3}>
          <Grid item>
            <Typography variant="body1" color="secondary">
              You can set the missing values above (except URL) in a file named aa.yaml in your repo, as shown in the
              example below:
            </Typography>

            <div className={classes.code} data-testid="aa-yaml-snippet">
              <CodeSnippet
                text={ENTITY_YAML}
                language="yaml"
                showLineNumbers
                customStyle={{ background: 'inherit', fontSize: '115%' }}
              />
            </div>

            <Button
              variant="contained"
              size="medium"
              color="primary"
              target="_blank"
              href="https://developer.aa.com/docs/#/getting-started/userguides/catalog?id=work-in-progress-more-metadata-aayaml"
            >
              Read more
            </Button>
          </Grid>
        </Grid>
      ) : null}
    </Grid>
  );
};
